import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import {
    LogOut,
    User,
    Calendar,
    Activity,
    Clock,
    TrendingUp,
    CheckCircle,
    ArrowRight,
    Users,
    LayoutDashboard,
    FileText,
    Download,
    Eye,
    Mail,
    Phone,
    Award,
    MapPin,
    MessageSquare,
    ChevronLeft,
    ChevronRight,
    Search,
    Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [applicants, setApplicants] = useState([]);
    const [loadingApplicants, setLoadingApplicants] = useState(false);
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        // Check if user is logged in
        const userData = localStorage.getItem('user');
        if (!userData) {
            navigate('/login');
            return;
        }

        try {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);
        } catch (error) {
            navigate('/login');
        }
    }, [navigate]);

    useEffect(() => {
        if (activeTab === 'applicants') {
            fetchApplicants();
        }
    }, [activeTab]);

    const fetchApplicants = async () => {
        setLoadingApplicants(true);
        try {
            const response = await axios.get(`${API}/applicants`);
            setApplicants(response.data);
        } catch (error) {
            console.error('Error fetching applicants:', error);
            toast({
                title: "Error",
                description: "No se pudieron cargar los aplicantes",
                variant: "destructive"
            });
        } finally {
            setLoadingApplicants(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('user');
        localStorage.removeItem('rememberMe');

        toast({
            title: "Sesión cerrada",
            description: "¡Hasta pronto!",
        });

        setTimeout(() => {
            navigate('/login');
        }, 500);
    };

    const handleDownloadCV = async (applicant) => {
        try {
            const response = await axios.get(`${API}/applicants/${applicant.id}/download-cv`);
            const { cvUrl, applicantName } = response.data;

            if (cvUrl) {
                // Open CV URL in a new tab
                window.open(cvUrl, '_blank');
                toast({
                    title: "Descargando CV",
                    description: `Abriendo CV de ${applicantName}`,
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "No se pudo descargar el CV",
                variant: "destructive"
            });
        }
    };

    const handleUpdateStatus = async (applicantId, newStatus) => {
        try {
            await axios.patch(`${API}/applicants/${applicantId}`, { status: newStatus });
            toast({
                title: "Estado actualizado",
                description: `El estado del aplicante ha sido actualizado a ${newStatus}`,
            });
            fetchApplicants();
        } catch (error) {
            toast({
                title: "Error",
                description: "No se pudo actualizar el estado",
                variant: "destructive"
            });
        }
    };

    const filteredApplicants = applicants.filter(applicant => {
        const matchesSearch = applicant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            applicant.email?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || applicant.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const getStatusColor = (status) => {
        const colors = {
            pending: 'bg-yellow-100 text-yellow-800',
            reviewed: 'bg-blue-100 text-blue-800',
            contacted: 'bg-purple-100 text-purple-800',
            rejected: 'bg-red-100 text-red-800',
            hired: 'bg-green-100 text-green-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusLabel = (status) => {
        const labels = {
            pending: 'Pendiente',
            reviewed: 'Revisado',
            contacted: 'Contactado',
            rejected: 'Rechazado',
            hired: 'Contratado'
        };
        return labels[status] || status;
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
            </div>
        );
    }

    const stats = [
        {
            title: 'Total Sessions',
            value: '12',
            icon: Calendar,
            change: '+2 this week',
            color: 'text-blue-600',
            bgColor: 'bg-blue-50'
        },
        {
            title: 'Active Plans',
            value: '3',
            icon: Activity,
            change: 'In progress',
            color: 'text-teal-600',
            bgColor: 'bg-teal-50'
        },
        {
            title: 'Hours Completed',
            value: '18.5',
            icon: Clock,
            change: '+3.5 this month',
            color: 'text-purple-600',
            bgColor: 'bg-purple-50'
        },
        {
            title: 'Progress',
            value: '87%',
            icon: TrendingUp,
            change: '+12% improvement',
            color: 'text-green-600',
            bgColor: 'bg-green-50'
        }
    ];

    const sidebarItems = [
        { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'applicants', label: 'Aplicantes', icon: Users },
    ];

    const renderOverview = () => (
        <>
            {/* Welcome Section */}
            <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    ¡Bienvenido de vuelta! 👋
                </h2>
                <p className="text-gray-600">
                    Aquí tienes un resumen de tu progreso de rehabilitación
                </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <Card key={index} className="p-6 hover:shadow-lg transition-all duration-300 border-gray-200">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                                    <p className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</p>
                                    <p className="text-xs text-gray-500">{stat.change}</p>
                                </div>
                                <div className={`${stat.bgColor} p-3 rounded-xl`}>
                                    <Icon className={`w-6 h-6 ${stat.color}`} />
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {/* Quick Actions */}
            <div className="mt-8">
                <Card className="p-8 border-gray-200 bg-gradient-to-br from-teal-50 to-white">
                    <div className="text-center">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            ¿Listo para tu próxima sesión?
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Reserva una nueva cita o reprograma una existente
                        </p>
                        <Button
                            onClick={() => navigate('/booking')}
                            className="h-12 px-8 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg shadow-lg transition-all duration-300"
                        >
                            Reservar Nueva Sesión
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                    </div>
                </Card>
            </div>
        </>
    );

    const renderApplicants = () => (
        <>
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Lista de Aplicantes
                </h2>
                <p className="text-gray-600">
                    Gestiona y revisa los candidatos que han aplicado
                </p>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por nombre o email..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        data-testid="search-applicants-input"
                    />
                </div>
                <select
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="status-filter-select"
                >
                    <option value="all">Todos los estados</option>
                    <option value="pending">Pendiente</option>
                    <option value="reviewed">Revisado</option>
                    <option value="contacted">Contactado</option>
                    <option value="rejected">Rechazado</option>
                    <option value="hired">Contratado</option>
                </select>
            </div>

            {/* Applicants List */}
            {loadingApplicants ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
                </div>
            ) : filteredApplicants.length === 0 ? (
                <Card className="p-12 text-center border-gray-200">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay aplicantes</h3>
                    <p className="text-gray-500">Los aplicantes aparecerán aquí cuando se registren</p>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {filteredApplicants.map((applicant) => (
                        <Card
                            key={applicant.id}
                            className="p-6 border-gray-200 hover:shadow-lg transition-all duration-300"
                            data-testid={`applicant-card-${applicant.id}`}
                        >
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                {/* Applicant Info */}
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center">
                                            <span className="text-white font-bold text-lg">
                                                {applicant.name?.charAt(0)?.toUpperCase() || 'A'}
                                            </span>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 text-lg">{applicant.name}</h3>
                                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(applicant.status)}`}>
                                                {getStatusLabel(applicant.status)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Mail className="w-4 h-4 text-gray-400" />
                                            <span>{applicant.email}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                            <span>{applicant.phone}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Award className="w-4 h-4 text-gray-400" />
                                            <span>{applicant.license_type} - {applicant.license_number}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                            <span>Estado: {applicant.license_state}</span>
                                        </div>
                                        {applicant.message && (
                                            <div className="flex items-center gap-2 text-gray-600 sm:col-span-2">
                                                <MessageSquare className="w-4 h-4 text-gray-400" />
                                                <span className="truncate">{applicant.message}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex flex-wrap gap-2 lg:flex-col lg:w-auto">
                                    {applicant.cv_url && (
                                        <Button
                                            onClick={() => handleDownloadCV(applicant)}
                                            className="bg-teal-600 hover:bg-teal-700 text-white"
                                            size="sm"
                                        >
                                            <Download className="w-4 h-4 mr-2" />
                                            Descargar CV
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => setSelectedApplicant(applicant)}
                                        variant="outline"
                                        size="sm"
                                        data-testid={`view-details-btn-${applicant.id}`}
                                    >
                                        <Eye className="w-4 h-4 mr-2" />
                                        Ver Detalles
                                    </Button>
                                    <select
                                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                                        value={applicant.status}
                                        onChange={(e) => handleUpdateStatus(applicant.id, e.target.value)}
                                        data-testid={`status-select-${applicant.id}`}
                                    >
                                        <option value="pending">Pendiente</option>
                                        <option value="reviewed">Revisado</option>
                                        <option value="contacted">Contactado</option>
                                        <option value="rejected">Rechazado</option>
                                        <option value="hired">Contratado</option>
                                    </select>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Detail Modal */}
            {selectedApplicant && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="applicant-detail-modal">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-900">Detalles del Aplicante</h2>
                                <Button
                                    variant="ghost"
                                    onClick={() => setSelectedApplicant(null)}
                                    data-testid="close-modal-btn"
                                >
                                    ✕
                                </Button>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center gap-4 pb-4 border-b">
                                    <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center">
                                        <span className="text-white font-bold text-3xl">
                                            {selectedApplicant.name?.charAt(0)?.toUpperCase() || 'A'}
                                        </span>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900">{selectedApplicant.name}</h3>
                                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedApplicant.status)}`}>
                                            {getStatusLabel(selectedApplicant.status)}
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="p-4 bg-gray-50 rounded-lg">
                                        <p className="text-sm text-gray-500 mb-1">Email</p>
                                        <p className="font-medium text-gray-900 flex items-center gap-2">
                                            <Mail className="w-4 h-4 text-teal-600" />
                                            {selectedApplicant.email}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-gray-50 rounded-lg">
                                        <p className="text-sm text-gray-500 mb-1">Teléfono</p>
                                        <p className="font-medium text-gray-900 flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-teal-600" />
                                            {selectedApplicant.phone}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-gray-50 rounded-lg">
                                        <p className="text-sm text-gray-500 mb-1">Tipo de Licencia</p>
                                        <p className="font-medium text-gray-900 flex items-center gap-2">
                                            <Award className="w-4 h-4 text-teal-600" />
                                            {selectedApplicant.license_type}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-gray-50 rounded-lg">
                                        <p className="text-sm text-gray-500 mb-1">Número de Licencia</p>
                                        <p className="font-medium text-gray-900 flex items-center gap-2">
                                            <FileText className="w-4 h-4 text-teal-600" />
                                            {selectedApplicant.license_number}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-gray-50 rounded-lg">
                                        <p className="text-sm text-gray-500 mb-1">Estado de Licencia</p>
                                        <p className="font-medium text-gray-900 flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-teal-600" />
                                            {selectedApplicant.license_state}
                                        </p>
                                    </div>
                                    {selectedApplicant.cv_url && (
                                        <div className="p-4 bg-gray-50 rounded-lg">
                                            <p className="text-sm text-gray-500 mb-1">CV</p>
                                            <Button
                                                onClick={() => handleDownloadCV(selectedApplicant)}
                                                size="sm"
                                                className="bg-teal-600 hover:bg-teal-700"
                                            >
                                                <Download className="w-4 h-4 mr-2" />
                                                Descargar CV
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {selectedApplicant.message && (
                                    <div className="p-4 bg-gray-50 rounded-lg">
                                        <p className="text-sm text-gray-500 mb-1">Mensaje</p>
                                        <p className="text-gray-900">{selectedApplicant.message}</p>
                                    </div>
                                )}

                                <div className="pt-4 border-t flex justify-end gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={() => setSelectedApplicant(null)}
                                    >
                                        Cerrar
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </>
    );

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar */}
            <aside
                className={`bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'} fixed h-full z-40`}
                data-testid="sidebar"
            >
                {/* Sidebar Header */}
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    {!sidebarCollapsed && (
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl shadow-md flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-white" />
                            </div>
                            <span className="font-bold text-gray-900">ATS System</span>
                        </div>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className="p-2"
                        data-testid="toggle-sidebar-btn"
                    >
                        {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                    </Button>
                </div>

                {/* Navigation Items */}
                <nav className="flex-1 p-4 space-y-2">
                    {sidebarItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeTab === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => setActiveTab(item.id)}
                                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${isActive
                                    ? 'bg-teal-50 text-teal-700 font-medium'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                data-testid={`nav-${item.id}`}
                            >
                                <Icon className={`w-5 h-5 ${isActive ? 'text-teal-600' : 'text-gray-500'}`} />
                                {!sidebarCollapsed && <span>{item.label}</span>}
                            </button>
                        );
                    })}
                </nav>

                {/* User Section at Bottom */}
                {!sidebarCollapsed && (
                    <div className="p-4 border-t border-gray-200">
                        <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-gray-50 rounded-lg">
                            <User className="w-5 h-5 text-gray-600" />
                            <span className="text-sm text-gray-700 truncate">{user.email}</span>
                        </div>
                        <Button
                            onClick={handleLogout}
                            variant="outline"
                            className="w-full border-2 border-gray-300 hover:border-red-600 hover:text-red-600 transition-all duration-200"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Cerrar Sesión
                        </Button>
                    </div>
                )}
            </aside>

            {/* Main Content */}
            <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
                {/* Header */}
                <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
                    <div className="px-6 py-4 flex items-center justify-between">
                        <h1 className="text-xl font-bold text-gray-900">
                            {activeTab === 'overview' ? 'Dashboard' : 'Aplicantes'}
                        </h1>
                        <div className="flex items-center gap-4">
                            {activeTab === 'applicants' && (
                                <Button
                                    onClick={fetchApplicants}
                                    variant="outline"
                                    size="sm"
                                    data-testid="refresh-applicants-btn"
                                >
                                    Actualizar
                                </Button>
                            )}
                        </div>
                    </div>
                </header>

                {/* Content Area */}
                <div className="p-6">
                    {activeTab === 'overview' && renderOverview()}
                    {activeTab === 'applicants' && renderApplicants()}
                </div>
            </main>
        </div>
    );
};

export default Dashboard;
